import React, { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { associateTotpToken, verifyAndEnableTotp, buildTotpUri } from '../auth/totp.js'
import { TextField, Button } from '../ui'

/**
 * Blocking, non-dismissible TOTP enrollment modal shown when the user's
 * org has ``mfaEnforced`` on and the user has not set up TOTP yet.
 *
 * Passkeys do not satisfy enforcement — only TOTP.
 */
export function MfaEnforcementModal({ user, onEnrolled }) {
  const [secret, setSecret] = useState(null)
  const [qrDataUrl, setQrDataUrl] = useState(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [starting, setStarting] = useState(true)

  useEffect(() => {
    let cancelled = false
    associateTotpToken(user.accessToken)
      .then((s) => { if (!cancelled) setSecret(s) })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to start TOTP setup') })
      .finally(() => { if (!cancelled) setStarting(false) })
    return () => { cancelled = true }
  }, [user.accessToken])

  useEffect(() => {
    if (secret && user.email) {
      QRCode.toDataURL(buildTotpUri(secret, user.email), { width: 160, margin: 1 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(null))
    }
  }, [secret, user.email])

  async function handleVerify() {
    setError(null)
    setLoading(true)
    try {
      await verifyAndEnableTotp(user.accessToken, code.trim())
      onEnrolled?.()
    } catch (err) {
      setError(err.message || 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="mfa-enforce-title">
      <div style={styles.card}>
        <h2 id="mfa-enforce-title" style={styles.title}>MFA required</h2>
        <p style={styles.body}>
          Your organization requires multi-factor authentication. Scan the QR
          code with an authenticator app (e.g. 1Password, Authy, Google
          Authenticator) and enter the 6-digit code below to continue.
        </p>
        {starting && <div style={styles.muted}>Preparing setup…</div>}
        {secret && (
          <>
            <div style={styles.qrWrap}>
              {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR code" width="160" height="160" />}
            </div>
            <div style={styles.secretRow}><code style={styles.secret}>{secret}</code></div>
            <form
              style={styles.verifyRow}
              onSubmit={(e) => { e.preventDefault(); if (code.length === 6 && !loading) handleVerify() }}
            >
              <div style={{ flex: 1 }}>
                <TextField
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  aria-label="6-digit verification code"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                loading={loading}
                loadingLabel="Verifying…"
              >
                Verify
              </Button>
            </form>
          </>
        )}
        {error && <div style={styles.error}>{error}</div>}
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: 24,
  },
  card: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 10, padding: 24, width: '100%', maxWidth: 420,
    boxShadow: 'var(--shadow-lg)',
  },
  title: { fontSize: 16, fontWeight: 700, margin: '0 0 12px 0' },
  body: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 16px 0' },
  muted: { fontSize: 12, color: 'var(--text-secondary)' },
  qrWrap: { display: 'flex', justifyContent: 'center', padding: 12, background: '#fff', borderRadius: 8 },
  secretRow: { display: 'flex', justifyContent: 'center', margin: '10px 0' },
  secret: { fontSize: 12, fontFamily: 'ui-monospace, monospace', background: 'var(--bg)', padding: '4px 8px', borderRadius: 4 },
  verifyRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 },
  error: { marginTop: 10, fontSize: 12, color: 'var(--danger, #c00)' },
}
